import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Menu, MenuItem } from '@mui/material';
import type { GridColumnHeaderParams } from '@mui/x-data-grid';
import { GridHeaderCheckbox, useGridApiContext } from '@mui/x-data-grid-pro';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import type { RowSelectionMode } from '~/hooks/data-grid';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface SelectAllHeaderCheckboxProps extends GridColumnHeaderParams<any, any, any> {
  handleRowSelectionModeChange: (mode: RowSelectionMode) => void;
  selectableCount: number;
  rowSelectionMode: RowSelectionMode;
}

const MENU_ID = 'selection-mode-menu';

export const SelectAllHeaderCheckbox = forwardRef<HTMLButtonElement, SelectAllHeaderCheckboxProps>(
  function SelectAllHeaderCheckbox(
    { handleRowSelectionModeChange, selectableCount, rowSelectionMode, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const apiRef = useGridApiContext();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = anchorEl !== null;

    const handleMenuItemClick = (mode: RowSelectionMode) => {
      handleRowSelectionModeChange(mode);
      apiRef.current.publishEvent('headerSelectionCheckboxChange', { value: true });
      setAnchorEl(null);
    };

    return (
      <>
        <ButtonGroup
          variant="outlined"
          sx={{
            backgroundColor: 'white',
          }}
        >
          {/* A plain box, not a Button: a checkbox nested inside a button is two controls in one. */}
          <Box
            sx={(theme) => ({
              alignItems: 'center',
              border: 1,
              borderColor: 'borders.control',
              borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
              display: 'flex',
              height: '35px',
              justifyContent: 'center',
              minWidth: '30px',
            })}
          >
            <GridHeaderCheckbox {...props} {...ref} />
          </Box>
          <Tooltip
            describeChild={false}
            title={t('common.selection_mode', {
              mode: t(`common.selection_mode_${rowSelectionMode}`),
            })}
            placement="right"
          >
            <Button
              aria-controls={open ? MENU_ID : undefined}
              aria-expanded={open}
              aria-haspopup="menu"
              disableRipple
              size="small"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              // The grid treats keys inside this header as its own and cancels Enter and Space,
              // which would stop the button from activating; keep them to the button.
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
              }}
              sx={{
                border: 1,
                borderColor: 'borders.control',
                borderLeft: 0,
                height: '35px',
                minWidth: '30px !important',
                paddingX: 0,
                '&:hover': {
                  border: 1,
                  borderColor: 'borders.control',
                  borderLeft: 0,
                },
              }}
            >
              <KeyboardArrowDown />
            </Button>
          </Tooltip>
        </ButtonGroup>

        {/* A Menu, so the keyboard reaches it: focus moves in, the arrow keys walk it, and Escape
            closes it and hands focus back to the button. */}
        <Menu
          anchorEl={anchorEl}
          id={MENU_ID}
          onClose={() => setAnchorEl(null)}
          open={open}
          slotProps={{ paper: { sx: { width: '250px' } } }}
        >
          <MenuItem
            selected={rowSelectionMode === 'page'}
            onClick={() => handleMenuItemClick('page')}
          >
            {t('common.select_this_page_only', { count: selectableCount })}
          </MenuItem>
          <MenuItem
            selected={rowSelectionMode === 'all'}
            onClick={() => handleMenuItemClick('all')}
          >
            {t('common.select_all')}
          </MenuItem>
        </Menu>
      </>
    );
  },
);
